import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    LoadingDotsMeasuresConstantType,
    LoadingDotsTonesConstantType,
    LoadingDotsSizeConstantType,
    LoadingDotsDynamicStyleType,
    LoadingDotsMeasures,
    LoadingDotsMotion,
    LoadingDotsTones,
    LoadingDotsSize
} from "./type";
import {
    LOADING_DOTS_RADIUS_DIVISOR,
    LOADING_DOTS_MOTIONS,
    LOADING_DOTS_SIZES
} from "./constant";
import {
    getTypeColors
} from "../../../../helpers/theme/typeColors";
import type {
    Mutable
} from "../../../../types";
import {
    getStateColorKey
} from "../../../../helpers/theme/state";
import {
    webStyle
} from "../../../../utils";

export const getLoadingDotsSize = ({
    iconSizes,
    size
}: LoadingDotsSizeConstantType): LoadingDotsSize => {
    if(size === "small") {
        return "small";
    }

    if(typeof size === "number" && size <= iconSizes.small) {
        return "small";
    }

    return "large";
};

export const getLoadingDotsMeasures = ({
    radiuses,
    borders,
    columns,
    size
}: LoadingDotsMeasuresConstantType): LoadingDotsMeasures => {
    const spec = LOADING_DOTS_SIZES[size];

    const dot = borders.indicator * spec.scale;
    const gap = borders[spec.gap];

    return {
        radius: Math.min(radiuses.form / LOADING_DOTS_RADIUS_DIVISOR, dot / 2),
        side: columns * dot + (columns - 1) * gap,
        gap,
        dot
    };
};

export const getLoadingDotsTones = ({
    customColor,
    surface,
    colors,
    type
}: LoadingDotsTonesConstantType): LoadingDotsTones => {
    const stateKey = getStateColorKey({
        type
    });

    const filled = getTypeColors({
        variant: "filled",
        colors,
        type
    });

    const outline = getTypeColors({
        variant: "outline",
        colors,
        type
    });

    const tonal = getTypeColors({
        variant: "tonal",
        colors,
        type
    });

    const tones: LoadingDotsTones = {
        dim: colors.content.border.subtle,
        mid: colors.content.icon.low,
        dark: filled.containerColor
    };

    if(surface === "filled") {
        Object.assign(tones, {
            mid: colors.system.high[stateKey],
            dark: filled.titleColor,
            dim: outline.borderColor
        });
    }

    if(surface === "tonal") {
        Object.assign(tones, {
            dim: colors.system.high[stateKey],
            mid: outline.borderColor,
            dark: tonal.titleColor
        });
    }

    if(customColor) {
        const projectColor = colors.project[customColor as keyof NCoreUIKit.ProjectColorPalette];

        tones.dark = projectColor ? projectColor : customColor;
    }

    return tones;
};

const getLoadingDotsKeyframes = (motion: LoadingDotsMotion, tone: "mid" | "dark") => {
    const keyframes: Record<string, {
        opacity: number;
    }> = {};

    LOADING_DOTS_MOTIONS[motion].stops.forEach((stop) => {
        keyframes[`${stop.at * 100}%`] = {
            opacity: tone === "dark" ? Number(stop.tone === "dark") : Number(stop.tone !== "dim")
        };
    });

    return keyframes;
};

const createWebLayers = (motion: LoadingDotsMotion) => StyleSheet.create({
    dark: webStyle({
        animationKeyframes: getLoadingDotsKeyframes(motion, "dark"),
        animationIterationCount: "infinite",
        animationTimingFunction: "linear"
    }),
    mid: webStyle({
        animationKeyframes: getLoadingDotsKeyframes(motion, "mid"),
        animationIterationCount: "infinite",
        animationTimingFunction: "linear"
    })
});

export const LOADING_DOTS_WEB_LAYERS = {
    trail: createWebLayers("trail"),
    wave: createWebLayers("wave"),
    fill: createWebLayers("fill")
};

const stylesheet = StyleSheet.create({
    layer: {
        ...StyleSheet.absoluteFill
    },
    container: {
        flexDirection: "row",
        flexWrap: "wrap",
        flexShrink: 0
    },
    dot: {
        overflow: "hidden"
    }
});

export const useStyles = ({
    measures,
    tones
}: LoadingDotsDynamicStyleType) => {
    const styles = {
        dot: {
            borderRadius: measures.radius,
            backgroundColor: tones.dim,
            height: measures.dot,
            width: measures.dot
        } as Mutable<ViewStyle>,
        dark: {
            backgroundColor: tones.dark
        } as Mutable<ViewStyle>,
        mid: {
            backgroundColor: tones.mid
        } as Mutable<ViewStyle>,
        container: {
            columnGap: measures.gap,
            rowGap: measures.gap,
            width: measures.side
        } as Mutable<ViewStyle>
    };

    return styles;
};
export default stylesheet;
